import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, mutate } from '../shared/api';
import { SHELL_MODULE_REGISTRY, type ModuleKey } from '../shared/biModuleRegistry';
import { canOpenModule } from '../shared/biModuleKeys';
import { canOpenAdmin } from '../shared/foundationFilters';
import { moduleAvatar } from '../shared/moduleAvatars';
import { formatMoney, formatMoneyAxis, formatNumber, formatDate } from '../shared/formatting';
import { NiceLineChart, Sparkline, Treemap, RankedBar, BulletChart } from '../shared/charts';
import type { User, FoundationPayload, CashflowPayload, LoansPayload, Filters } from '../types/biApp';

type Locale = 'hr' | 'en';
type Task = { id: number; title: string; due_date: string | null; priority: string };
type MyTasks = { overdue: Task[]; today: Task[]; upcoming: Task[]; no_date: Task[] };

function Avatar({ keyName, size = 56 }: { keyName: string; size?: number }) {
    const a = moduleAvatar(keyName);
    return (
        <div style={{
            width: size, height: size, borderRadius: size * 0.28, flex: 'none',
            background: `linear-gradient(135deg, ${a.grad[0]}, ${a.grad[1]})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: size * 0.5, boxShadow: '0 4px 12px rgba(107,79,28,0.28)', color: '#fff',
        }}>{a.icon}</div>
    );
}

function deltaTone(value: number): 'pos' | 'neg' | 'neutral' {
    if (value > 0) return 'pos';
    if (value < 0) return 'neg';
    return 'neutral';
}

/* Radial progress ring - SVG donut for KPI visual weight */
function Ring({ pct, size = 56, stroke = 5, color = 'var(--ad-acc)' }: { pct: number; size?: number; stroke?: number; color?: string }) {
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ * (1 - Math.min(1, Math.max(0, pct)));
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="lv2-ring">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(var(--ad-soft-rgb),0.12)" strokeWidth={stroke} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
                strokeDasharray={circ} strokeDashoffset={offset}
                strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
        </svg>
    );
}

/* Priority indicator dot */
function PriorityDot({ priority }: { priority: string }) {
    const cls = priority === 'high' ? 'lv2-prio-high' : priority === 'medium' ? 'lv2-prio-med' : 'lv2-prio-low';
    return <span className={`lv2-prio-dot ${cls}`} />;
}

/* Task card - visual card instead of boring list row */
function TaskCard({ task, isOverdue, onClick, fmtDue }: { task: Task; isOverdue: boolean; onClick: () => void; fmtDue: (d: string | null) => string }) {
    const dueStr = fmtDue(task.due_date);
    return (
        <button type="button" className={`lv2-task-card ${isOverdue ? 'lv2-task-card-overdue' : ''}`} onClick={onClick}>
            <div className="lv2-task-card-left">
                <PriorityDot priority={task.priority} />
                <span className="lv2-task-card-title">{task.title}</span>
            </div>
            <div className="lv2-task-card-right">
                {dueStr && <span className="lv2-task-card-date">{dueStr}</span>}
                {isOverdue && <span className="lv2-task-card-flag">!</span>}
            </div>
        </button>
    );
}

export function LandingModuleV2({ user, token, locale, foundation, cashflow, loans, filters, onOpenModule, onShortcutsChange }: {
    user: User;
    token: string;
    locale: Locale;
    foundation: FoundationPayload;
    cashflow: CashflowPayload | null;
    loans: LoansPayload | null;
    filters: Filters;
    onOpenModule: (key: ModuleKey) => void;
    onShortcutsChange?: (next: string[]) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    // --- module list ---
    const modules = useMemo(() => SHELL_MODULE_REGISTRY.filter((e) => {
        if (e.key === 'dashboard' || e.parentKey || e.hiddenFromNav) return false;
        if (e.key === 'admin') return canOpenAdmin(user) && canOpenModule(user, 'admin');
        return canOpenModule(user, e.key);
    }).slice().sort((a, b) => a.code - b.code), [user]);

    const moduleByKey = useMemo(() => new Map(modules.map((m) => [m.key as string, m])), [modules]);
    const label = (key: string) => { const m = moduleByKey.get(key); return m ? (locale === 'en' ? m.labelEn : m.labelHr) : key; };

    // --- shortcuts ---
    const [shortcuts, setShortcuts] = useState<string[]>(() => (user.landing_shortcuts ?? []).filter(() => true));
    const [pickerOpen, setPickerOpen] = useState(false);
    const [tasks, setTasks] = useState<MyTasks | null>(null);
    const [taskTab, setTaskTab] = useState<'overdue' | 'upcoming' | 'all'>('all');

    useEffect(() => { setShortcuts((prev) => prev.filter((k) => moduleByKey.has(k))); }, [moduleByKey]);
    useEffect(() => { void api<MyTasks>('/tasks/my-tasks', token).then(setTasks).catch(() => setTasks(null)); }, [token]);

    const persist = useCallback((next: string[]) => {
        setShortcuts(next);
        onShortcutsChange?.(next);
        void mutate('/landing/shortcuts', token, 'PUT', { shortcuts: next }).catch(() => undefined);
    }, [token, onShortcutsChange]);

    const addShortcut = (key: string) => { if (shortcuts.includes(key) || shortcuts.length >= 10) return; persist([...shortcuts, key]); };
    const removeShortcut = (key: string) => persist(shortcuts.filter((k) => k !== key));
    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir; if (j < 0 || j >= shortcuts.length) return;
        const n = shortcuts.slice(); [n[i], n[j]] = [n[j], n[i]]; persist(n);
    };
    const addable = modules.filter((m) => !shortcuts.includes(m.key as string));

    // --- derived data ---
    const consolidatedClosing = cashflow?.balances.consolidated.closing ?? 0;
    const consolidatedChange = cashflow?.balances.consolidated.change ?? 0;
    const consolidatedOpening = cashflow?.balances.consolidated.opening ?? 0;
    const loansBalance = loans?.balances.closing ?? 0;
    const loansChange = loans?.balances.change ?? 0;
    const totalEntries = cashflow?.summary.entries ?? 0;
    const totalDebit = cashflow?.summary.debit ?? 0;
    const totalCredit = cashflow?.summary.credit ?? 0;
    const grossTurnover = totalDebit + totalCredit;
    const periodLabel = `${filters.dateFrom ?? '-'} - ${filters.dateTo ?? '-'}`;

    const runningBalanceSeries = useMemo(() => {
        if (!cashflow?.graph?.length) return [] as Array<{ date: string; net: number }>;
        let running = consolidatedOpening;
        return cashflow.graph.map((point) => {
            running += point.net;
            return { date: point.date, net: running };
        });
    }, [cashflow?.graph, consolidatedOpening]);

    const sparkVals = useMemo(() => runningBalanceSeries.map((p) => p.net), [runningBalanceSeries]);

    // NiceLineChart series format
    const heroChartSeries = useMemo(() => {
        if (!sparkVals.length) return [];
        return [{ name: t('Saldo', 'Balance'), color: '#c99b31', values: sparkVals }];
    }, [sparkVals, locale]);

    const heroXLabels = useMemo(() => {
        if (!runningBalanceSeries.length) return [];
        return runningBalanceSeries.map((p) => {
            const d = new Date(p.date.replace(' ', 'T'));
            return `${d.getDate()}.${d.getMonth() + 1}`;
        });
    }, [runningBalanceSeries]);

    const topCompanyRows = useMemo(() => {
        const rows = cashflow?.companies ?? [];
        return [...rows].sort((a, b) => Math.abs(b.net ?? 0) - Math.abs(a.net ?? 0)).slice(0, 8);
    }, [cashflow?.companies]);

    // Treemap items for companies
    const treemapItems = useMemo(() => {
        if (!topCompanyRows.length) return [];
        const colors = ['#c99b31', '#b9871a', '#d6a84f', '#a87f1f', '#2f9e44', '#6f42c1', '#3b82f6', '#e67700'];
        return topCompanyRows.map((co, i) => ({
            label: co.code,
            value: Math.abs(co.net ?? 0),
            color: colors[i % colors.length],
        }));
    }, [topCompanyRows]);

    // RankedBar items
    const rankedBarItems = useMemo(() => {
        return topCompanyRows.map((co) => ({
            label: co.code,
            value: co.net ?? 0,
            color: (co.net ?? 0) >= 0 ? '#2f9e44' : '#b14040',
            sub: formatMoney(co.net, locale),
        }));
    }, [topCompanyRows, locale]);

    const sectorCount = foundation.companies.reduce((total, c) => total + c.sectors.length, 0);
    const departmentCount = foundation.companies.reduce((total, c) => total + c.sectors.reduce((s, sec) => s + sec.departments.length, 0), 0);

    // --- tasks ---
    const overdue = tasks?.overdue ?? [];
    const today = tasks?.today ?? [];
    const upcoming = tasks?.upcoming ?? [];
    const noDate = tasks?.no_date ?? [];
    const allTasks = [...overdue, ...today, ...upcoming, ...noDate];
    const totalTasks = allTasks.length;
    const fmtDue = (d: string | null) => d ? new Date(d.replace(' ', 'T')).toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit' }) : '';

    const visibleTasks = useMemo<
        { tasks: Task[]; areOverdue: boolean } | { mixed: Array<{ task: Task; isOverdue: boolean }> }
    >(() => {
        if (taskTab === 'overdue') return { tasks: overdue, areOverdue: true };
        if (taskTab === 'upcoming') return { tasks: [...today, ...upcoming].slice(0, 8), areOverdue: false };
        // 'all': show overdue first, then rest
        const items: Array<{ task: Task; isOverdue: boolean }> = [];
        overdue.forEach((tk) => items.push({ task: tk, isOverdue: true }));
        [...today, ...upcoming, ...noDate].slice(0, 8 - overdue.length).forEach((tk) => items.push({ task: tk, isOverdue: false }));
        return { mixed: items };
    }, [taskTab, overdue, today, upcoming, noDate]);

    // --- greeting ---
    const hour = new Date().getHours();
    const greeting = hour < 12 ? t('Dobro jutro', 'Good morning') : hour < 18 ? t('Dobar dan', 'Good afternoon') : t('Dobra vecer', 'Good evening');
    const firstName = (user.name ?? '').split(' ')[0] || '';

    // --- debit vs credit for bullet ---
    const debitCreditMax = Math.max(totalDebit, totalCredit, 1);

    return (
        <section className="lv2">
            {/* ===== HERO - DRAMATIC DARK GRADIENT ===== */}
            <div className="lv2-hero">
                <div className="lv2-hero-bg" aria-hidden="true">
                    <div className="lv2-hero-orb lv2-hero-orb-1" />
                    <div className="lv2-hero-orb lv2-hero-orb-2" />
                    <div className="lv2-hero-orb lv2-hero-orb-3" />
                </div>
                <div className="lv2-hero-inner">
                    <div className="lv2-hero-left">
                        <p className="lv2-greeting">{greeting}{firstName ? `, ${firstName}` : ''}</p>
                        <h1 className="lv2-hero-figure">
                            {cashflow ? formatMoney(consolidatedClosing, locale) : '...'}
                        </h1>
                        <p className="lv2-hero-sub">
                            {t('Konsolidirana bankovna pozicija', 'Consolidated bank position')}
                        </p>
                        <div className="lv2-hero-delta">
                            <span className={`lv2-delta-badge tone-${deltaTone(consolidatedChange)}`}>
                                {consolidatedChange >= 0 ? '+' : ''}{cashflow ? formatMoney(consolidatedChange, locale) : '-'}
                            </span>
                            <span className="lv2-period">{periodLabel}</span>
                        </div>
                    </div>
                    <div className="lv2-hero-right">
                        <div className="lv2-hero-chart">
                            {heroChartSeries.length > 0
                                ? <NiceLineChart series={heroChartSeries} height={160} locale={locale} xLabels={heroXLabels} yFormatter={formatMoneyAxis} />
                                : <div className="lv2-chart-placeholder">{t('Nema podataka', 'No data')}</div>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== KPI STRIP - LARGER, MORE VISUAL ===== */}
            <div className="lv2-kpi-strip">
                <article className="lv2-kpi" data-accent="green" onClick={() => onOpenModule('cashflow')}>
                    <div className="lv2-kpi-top-row">
                        <Ring pct={grossTurnover > 0 ? Math.min(1, totalCredit / grossTurnover) : 0} size={48} stroke={4} color="#2f9e44" />
                        <div className="lv2-kpi-nums">
                            <span className="lv2-kpi-label">{t('Bruto promet', 'Gross turnover')}</span>
                            <p className="lv2-kpi-value">{cashflow ? formatMoneyAxis(grossTurnover) : '...'}</p>
                        </div>
                    </div>
                    <div className="lv2-kpi-foot">
                        <span className="lv2-kpi-meta">{cashflow ? formatNumber(totalEntries) : '-'} {t('stavki', 'entries')}</span>
                        {sparkVals.length > 4 && <Sparkline values={sparkVals} width={90} height={26} color="#2f9e44" />}
                    </div>
                </article>

                <article className="lv2-kpi" data-accent="gold" onClick={() => onOpenModule('cashflow')}>
                    <div className="lv2-kpi-top-row">
                        <div className="lv2-kpi-debit-credit">
                            <div className="lv2-dc-bar">
                                <span className="lv2-dc-fill lv2-dc-debit" style={{ width: `${(totalDebit / debitCreditMax) * 100}%` }} />
                            </div>
                            <div className="lv2-dc-bar">
                                <span className="lv2-dc-fill lv2-dc-credit" style={{ width: `${(totalCredit / debitCreditMax) * 100}%` }} />
                            </div>
                        </div>
                        <div className="lv2-kpi-nums">
                            <span className="lv2-kpi-label">{t('Neto promjena', 'Net change')}</span>
                            <p className={`lv2-kpi-value tone-${deltaTone(consolidatedChange)}`}>
                                {cashflow ? formatMoney(consolidatedChange, locale) : '...'}
                            </p>
                        </div>
                    </div>
                    <div className="lv2-kpi-foot">
                        <span className="lv2-kpi-meta lv2-dc-legend">
                            <span className="lv2-dc-dot lv2-dc-dot-out" />{t('Izlaz', 'Out')}
                            <span className="lv2-dc-dot lv2-dc-dot-in" />{t('Ulaz', 'In')}
                        </span>
                    </div>
                </article>

                <article className="lv2-kpi" data-accent="purple" onClick={() => onOpenModule('loans')}>
                    <div className="lv2-kpi-top-row">
                        <Ring pct={loansBalance !== 0 ? 0.65 : 0} size={48} stroke={4} color="#6f42c1" />
                        <div className="lv2-kpi-nums">
                            <span className="lv2-kpi-label">{t('IC pozajmice', 'IC loans')}</span>
                            <p className={`lv2-kpi-value tone-${deltaTone(loansBalance)}`}>
                                {loans ? formatMoney(loansBalance, locale) : '...'}
                            </p>
                        </div>
                    </div>
                    <div className="lv2-kpi-foot">
                        <span className="lv2-kpi-meta">&Delta; {loans ? formatMoney(loansChange, locale) : '-'}</span>
                    </div>
                </article>

                <article className="lv2-kpi" data-accent="blue">
                    <div className="lv2-kpi-top-row">
                        <div className="lv2-kpi-stat-ring">
                            <span className="lv2-kpi-stat-num">{foundation.companies.length}</span>
                        </div>
                        <div className="lv2-kpi-nums">
                            <span className="lv2-kpi-label">{t('Master data', 'Master data')}</span>
                            <p className="lv2-kpi-value">{sectorCount} <small>{t('sektora', 'sectors')}</small></p>
                        </div>
                    </div>
                    <div className="lv2-kpi-foot">
                        <span className="lv2-kpi-meta">{departmentCount} {t('odjela', 'depts')}</span>
                    </div>
                </article>
            </div>

            {/* ===== THREE-COLUMN LAYOUT: TASKS | COMPANIES TREEMAP | COMPANIES RANKED ===== */}
            <div className="lv2-triple-row">
                {/* TASKS - card-based, tabbed */}
                <div className="lv2-panel lv2-tasks-panel">
                    <div className="lv2-panel-head">
                        <h2 className="lv2-section-title">
                            {t('Zadaci', 'Tasks')}
                            {totalTasks > 0 && <span className="lv2-task-count">{totalTasks}</span>}
                            {overdue.length > 0 && <span className="lv2-overdue-badge">{overdue.length} {t('kasni', 'late')}</span>}
                        </h2>
                    </div>
                    {/* Tab bar */}
                    <div className="lv2-task-tabs">
                        <button type="button" className={`lv2-task-tab ${taskTab === 'all' ? 'active' : ''}`}
                            onClick={() => setTaskTab('all')}>{t('Sve', 'All')}</button>
                        <button type="button" className={`lv2-task-tab ${taskTab === 'overdue' ? 'active' : ''}`}
                            onClick={() => setTaskTab('overdue')}>
                            {t('Kasni', 'Overdue')} {overdue.length > 0 && <span className="lv2-tab-badge">{overdue.length}</span>}
                        </button>
                        <button type="button" className={`lv2-task-tab ${taskTab === 'upcoming' ? 'active' : ''}`}
                            onClick={() => setTaskTab('upcoming')}>{t('Dolazi', 'Upcoming')}</button>
                    </div>
                    {/* Task cards */}
                    <div className="lv2-task-cards">
                        {totalTasks === 0 ? (
                            <div className="lv2-empty-state">
                                <div className="lv2-empty-icon">&#10003;</div>
                                <p>{t('Sve rijeseno.', 'All clear.')}</p>
                            </div>
                        ) : 'mixed' in visibleTasks ? (
                            visibleTasks.mixed.map((item) => (
                                <TaskCard key={item.task.id} task={item.task} isOverdue={item.isOverdue}
                                    onClick={() => onOpenModule('team_tasks')} fmtDue={fmtDue} />
                            ))
                        ) : (
                            visibleTasks.tasks.map((tk) => (
                                <TaskCard key={tk.id} task={tk} isOverdue={visibleTasks.areOverdue}
                                    onClick={() => onOpenModule('team_tasks')} fmtDue={fmtDue} />
                            ))
                        )}
                    </div>
                    <button className="lv2-panel-action" type="button" onClick={() => onOpenModule('team_tasks')}>
                        {t('Otvori sve zadatke', 'Open all tasks')} &rarr;
                    </button>
                </div>

                {/* COMPANIES - treemap + ranked */}
                <div className="lv2-panel lv2-companies-panel">
                    <div className="lv2-panel-head">
                        <h2 className="lv2-section-title">{t('Drustva - protok', 'Companies - flow')}</h2>
                    </div>
                    {topCompanyRows.length === 0 ? (
                        <p className="lv2-empty-inline">{t('Nema podataka.', 'No data.')}</p>
                    ) : (
                        <>
                            <div className="lv2-treemap-wrap">
                                <Treemap items={treemapItems} height={160} locale={locale} moneyFormatter={(n) => formatMoneyAxis(n)} />
                            </div>
                            <div className="lv2-ranked-wrap">
                                <RankedBar items={rankedBarItems} locale={locale} moneyFormatter={(n) => formatMoney(n, locale)} maxBars={6} />
                            </div>
                        </>
                    )}
                    <button className="lv2-panel-action" type="button" onClick={() => onOpenModule('cashflow')}>
                        {t('Cashflow modul', 'Cashflow module')} &rarr;
                    </button>
                </div>
            </div>

            {/* ===== DEBIT/CREDIT BULLET ===== */}
            {cashflow && (
                <div className="lv2-bullet-row">
                    <div className="lv2-bullet-item">
                        <BulletChart label={t('Izlaz (Debit)', 'Outflow (Debit)')} actual={totalDebit} max={debitCreditMax * 1.15}
                            locale={locale} valueFormatter={formatMoneyAxis} height={22}
                            zones={[{ until: debitCreditMax * 0.5, color: 'rgba(47,158,68,0.15)' }, { until: debitCreditMax * 0.8, color: 'rgba(201,155,49,0.15)' }, { until: debitCreditMax * 1.15, color: 'rgba(177,64,64,0.15)' }]} />
                    </div>
                    <div className="lv2-bullet-item">
                        <BulletChart label={t('Ulaz (Credit)', 'Inflow (Credit)')} actual={totalCredit} max={debitCreditMax * 1.15}
                            locale={locale} valueFormatter={formatMoneyAxis} height={22}
                            zones={[{ until: debitCreditMax * 0.5, color: 'rgba(177,64,64,0.15)' }, { until: debitCreditMax * 0.8, color: 'rgba(201,155,49,0.15)' }, { until: debitCreditMax * 1.15, color: 'rgba(47,158,68,0.15)' }]} />
                    </div>
                </div>
            )}

            {/* ===== SHORTCUTS - with OVERLAY PICKER ===== */}
            <div className="lv2-section">
                <div className="lv2-panel-head">
                    <h2 className="lv2-section-title">{t('Precaci', 'Shortcuts')}</h2>
                    {shortcuts.length < 10 && (
                        <button className="lv2-add-btn" type="button" onClick={() => setPickerOpen(true)}>
                            + {t('Dodaj modul', 'Add module')}
                        </button>
                    )}
                </div>
                {shortcuts.length === 0 ? (
                    <div className="lv2-empty-state" style={{ padding: '20px 0' }}>
                        <p>{t('Nema precaca. Dodaj omiljene module.', 'No shortcuts. Add favourite modules.')}</p>
                        <button className="lv2-add-btn" type="button" onClick={() => setPickerOpen(true)}>
                            + {t('Dodaj', 'Add')}
                        </button>
                    </div>
                ) : (
                    <div className="lv2-shortcuts-strip">
                        {shortcuts.map((key, i) => (
                            <div key={key} className="lv2-sc" onClick={() => onOpenModule(key as ModuleKey)}>
                                <Avatar keyName={key} size={40} />
                                <strong className="lv2-sc-label">{label(key)}</strong>
                                <div className="lv2-sc-actions" onClick={(e) => e.stopPropagation()}>
                                    {i > 0 && <button type="button" onClick={() => move(i, -1)} className="lv2-ctrl-btn">&laquo;</button>}
                                    {i < shortcuts.length - 1 && <button type="button" onClick={() => move(i, 1)} className="lv2-ctrl-btn">&raquo;</button>}
                                    <button type="button" onClick={() => removeShortcut(key)} className="lv2-ctrl-btn lv2-ctrl-del">&times;</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ===== SHORTCUT PICKER OVERLAY ===== */}
            {pickerOpen && (
                <div className="lv2-overlay" onClick={() => setPickerOpen(false)}>
                    <div className="lv2-picker" onClick={(e) => e.stopPropagation()}>
                        <div className="lv2-picker-head">
                            <h3>{t('Dodaj precac', 'Add shortcut')}</h3>
                            <button type="button" onClick={() => setPickerOpen(false)} className="lv2-picker-close">&times;</button>
                        </div>
                        <div className="lv2-picker-grid">
                            {addable.length === 0 ? (
                                <p className="lv2-empty-inline">{t('Svi moduli su vec dodani.', 'All modules already added.')}</p>
                            ) : addable.map((m) => (
                                <button key={m.key} type="button" className="lv2-picker-item" onClick={() => addShortcut(m.key as string)}>
                                    <Avatar keyName={m.key as string} size={44} />
                                    <span className="lv2-picker-name">{locale === 'en' ? m.labelEn : m.labelHr}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ===== MODULE GALLERY - DRAMATIC CARDS ===== */}
            <div className="lv2-section">
                <div className="lv2-panel-head">
                    <h2 className="lv2-section-title">{t('Svi moduli', 'All modules')}</h2>
                </div>
                <div className="lv2-mod-grid">
                    {modules.map((m) => {
                        const av = moduleAvatar(m.key as string);
                        return (
                            <button key={m.key} type="button" onClick={() => onOpenModule(m.key as ModuleKey)} className="lv2-mod-card"
                                style={{ '--mc-c1': av.grad[0], '--mc-c2': av.grad[1] } as React.CSSProperties}>
                                <div className="lv2-mod-card-glow" />
                                <Avatar keyName={m.key as string} size={48} />
                                <div className="lv2-mod-card-text">
                                    <strong>{locale === 'en' ? m.labelEn : m.labelHr}</strong>
                                    <span className="lv2-mod-lead">{locale === 'en' ? m.leadEn : m.leadHr}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
